<template>
    <StepModal
        :title="$t('runAiProcessModal.title')"
        ref="modal"
        :steps="steps"
        @on-hide="hide"
        @submit="submit"
    />
</template>
<script>
import StepModal from '../../../../../../components/new/modal/StepModal.vue';

import { httpClient } from "../../../../../../services/HttpClient";
import MessageNotificator from '../../../../../../services/MessageNotificator';
import { AiProcessFieldEnum } from '../../../../../enums/AiProcessFieldEnum';
import { AiProcessTypeEnum } from '../../../../../enums/AiProcessTypeEnum';
import { defineAsyncComponent } from 'vue';

const SelectProductsScopeStep = defineAsyncComponent(() => import('./steps/SelectProductsScopeStep.vue'));
const SelectActionStep = defineAsyncComponent(() => import('./steps/SelectActionStep.vue'));
const SelectFieldsStep = defineAsyncComponent(() => import('./steps/SelectFieldsStep.vue'));
const SetUserCommentStep = defineAsyncComponent(() => import('./steps/SetUserCommentStep.vue'));

export default {
    name: "RunAiProcessModal",
    components: {
        StepModal
    },
    emits: ['update:table'],
    props: {
        integration: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            type: AiProcessTypeEnum.GENERATE_CONTENT,
            fields: Object.values(AiProcessFieldEnum),
            userComment: null,
            selectedUuids: [],
            tokenBalance: null,
            filters: {},
            allChecked: false,
            totalProducts: 0,
            scope: 'selected',
        }
    },
    computed: {
        canProcessAllFiltered() {
            return this.allChecked && this.selectedUuids.length < this.totalProducts;
        },
        steps() {
            const steps = [
                {
                    name: this.$t('runAiProcessModal.steps.action'),
                    component: SelectActionStep,
                    description: ` — ${this.$t('runAiProcessModal.steps.actionDescription')}`,
                    props: {
                        type: this.type,
                        'onUpdate:type': (value) => { this.type = value },
                        tokenBalance: this.tokenBalance,
                    }
                },
                {
                    name: this.$t('runAiProcessModal.steps.fields'),
                    component: SelectFieldsStep,
                    description: ` — ${this.$t('runAiProcessModal.steps.fieldsDescription')}`,
                    props: {
                        fields: this.fields,
                        'onUpdate:fields': (value) => { this.fields = value },
                    }
                },
                {
                    name: this.$t('runAiProcessModal.steps.userComment'),
                    component: SetUserCommentStep,
                    description: ` — ${this.$t('runAiProcessModal.steps.userCommentDescription')}`,
                    props: {
                        userComment: this.userComment,
                        'onUpdate:userComment': (value) => { this.userComment = value },
                    }
                }
            ];

            if (this.canProcessAllFiltered) {
                steps.unshift({
                    name: this.$t('runAiProcessModal.steps.scope'),
                    component: SelectProductsScopeStep,
                    description: ` — ${this.$t('runAiProcessModal.steps.scopeDescription')}`,
                    props: {
                        scope: this.scope,
                        'onUpdate:scope': (value) => { this.scope = value },
                        selectedCount: this.selectedUuids.length,
                        totalCount: this.totalProducts,
                    }
                });
            }

            return steps;
        }
    },
    methods: {
        async show(selectedUuids, filters, allChecked, totalProducts) {
            this.selectedUuids = selectedUuids;
            this.filters = filters ?? {};
            this.allChecked = allChecked ?? false;
            this.totalProducts = totalProducts ?? 0;
            await this.fetchBalance();
            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
            this.type = AiProcessTypeEnum.GENERATE_CONTENT;
            this.fields = Object.values(AiProcessFieldEnum);
            this.userComment = null;
            this.selectedUuids = [];
            this.tokenBalance = null;
            this.filters = {};
            this.allChecked = false;
            this.totalProducts = 0;
            this.scope = 'selected';
            this.$refs.modal?.reset();
        },
        async fetchBalance() {
            const balanceResponse = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = balanceResponse.data.balance;
        },
        async submit() {
            const settings = {fields: this.fields};

            if (this.userComment) {
                settings.user_comment = this.userComment;
            }

            try {
                const productFilters = this.scope === 'all' && this.canProcessAllFiltered
                    ? this.filters
                    : {uuids: this.selectedUuids};

                await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes`, {
                    type: this.type,
                    settings,
                    ...productFilters,
                });

                new MessageNotificator().success(this.$t("alerts.success"));
                this.$emit('update:table');
                this.hide();
            } finally {
                this.$refs.modal.isLoading = false;
            }
        }
    }
}
</script>
